// This must be the first line in src/index.tsx:
import "./polyfills/";

import { Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import "./errorReporting";
import "./consoleOverrides";
import * as serviceWorker from "./serviceWorker";
import swConfig from "./swConfig";

import GA from "./components/Analytics/GoogleAnalytics";
import CookieConsent from "./components/CookieConsent";
import App from "./App/App";

import { SettingsManager } from "./contexts/SettingsManager/SettingsManager";
import "./index.css";
// import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";

import MachemetryProvider from "providers/MachemetryProvider";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { store } from "store/store";
import { Provider } from "react-redux";
import "./i18n";
import { StyledSpinner } from "pages/CustomerTilesPage/CustomerTilesPage";
import { darkTheme, lightTheme, ThemeManager } from "providers/ThemeManager";
import { useIsDarkMode } from "store/atoms";
import { createRoot } from "react-dom/client";
import { useAuth0 } from "@auth0/auth0-react";
import { Auth0Manager } from "contexts/AuthManager/Auth";
import { FlightHelloTestPage } from "FlightHelloTestPage";

/**
 * All the providers wrapping the <App/>
 * used in test-utils for creating a custom render function
 * to override react-testing-library's render function
 *
 * @param {*} { children }
 */
export const AllTheProviders = ({ children }) => {
  const [isDarkMode] = useIsDarkMode();

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/hello" element={<FlightHelloTestPage />} />
        <Route
          path="*"
          element={
            <ThemeManager theme={isDarkMode ? darkTheme : lightTheme}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Provider store={store}>
                  <Auth0Manager>
                    <Suspense
                      fallback={<StyledSpinner className="index-spinner" />}
                    >
                      <SettingsManager>
                        <MachemetryProvider>{children}</MachemetryProvider>
                      </SettingsManager>
                    </Suspense>
                  </Auth0Manager>
                </Provider>
              </LocalizationProvider>
            </ThemeManager>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

waitForLocalStorage(() => {
  const Root = () => (
    // <React.StrictMode>
    <Suspense fallback={null}>
      <AllTheProviders>
        <>
          <App />
          <CookieConsent />
          <GATrackNonRavenUsers />
        </>
      </AllTheProviders>
    </Suspense>

    // </React.StrictMode>
  );

  // Concurrent React
  createRoot(
    document.getElementById("root") || document.createElement("div") // for testing purposes
  ).render(<Root />);

  serviceWorker.register(swConfig);
});

function GATrackNonRavenUsers() {
  const { isAuthenticated, user } = useAuth0();
  useEffect(() => {
    if (user && isAuthenticated) {
      // if it's a raven user, don't track them
      const userEmail = user?.email ?? user?.["https://raven.ai/email"];
      const userEmailDomain = userEmail?.split("@")[1];
      const isAdminUser = Boolean(
        userEmailDomain &&
          ["raventelemetry.com", "raven.ai"].includes(userEmailDomain)
      );
      if (isAdminUser) {
        return;
      }
      GA.init({
        gaOptions: { userId: user.email },
      });
    } else {
      GA.init();
    }
  }, [isAuthenticated, user]);

  return null;
}

function waitForLocalStorage(callback, retries = 20, interval = 100) {
  if (
    typeof localStorage !== "undefined" &&
    typeof localStorage.length !== "undefined"
  ) {
    callback();
  } else if (retries > 0) {
    console.log(
      "localStorage is not available, retrying... retries remaining:",
      retries
    );
    setTimeout(
      () => waitForLocalStorage(callback, retries - 1, interval),
      interval
    );
  } else {
    console.error("localStorage is not available");
  }
}
